// src/interaction/useInteractionManager.ts
import { useCallback, useEffect, useRef } from 'react';
import { useModelStore } from 'src/stores/modelStore';
import { useUiStateStore } from 'src/stores/uiStateStore';
import { useSceneStore } from 'src/stores/sceneStore';
import { ModeActions, State, SlimMouseEvent, Mode, ItemReference, ModelItem, ViewItem, Connector as ConnectorModel, Rectangle as RectangleModel, TextBox as TextBoxModel } from 'src/types';
import { getMouse, getItemsAtTile, hasMovedTile, getWheelZoom, getZoomedScrollPosition, generateId } from 'src/utils';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import { useScene } from 'src/hooks/useScene';
import { Cursor } from './modes/Cursor';
import { DragItems } from './modes/DragItems';
import { DrawRectangle } from './modes/Rectangle/DrawRectangle';
import { TransformRectangle } from './modes/Rectangle/TransformRectangle';
import { Connector } from './modes/Connector';
import { Pan } from './modes/Pan';
import { PlaceIcon, requestIconReplacement } from './modes/PlaceIcon';
import { TextBox } from './modes/TextBox';
import { useGroupStore } from '@/stores/groupStore';
import { GroupableElement } from '@/features/groups/groupTypes';
import { iconPackManager } from '@/features/components/iconPacks';
import { MAX_ZOOM, MIN_ZOOM, VIEW_ITEM_DEFAULTS } from '@/config';

const modes: { [k in string]: ModeActions } = {
  CURSOR: Cursor,
  DRAG_ITEMS: DragItems,
  'RECTANGLE.DRAW': DrawRectangle,
  'RECTANGLE.TRANSFORM': TransformRectangle,
  CONNECTOR: Connector,
  PAN: Pan,
  PLACE_ICON: PlaceIcon,
  TEXTBOX: TextBox
};

interface EditorClipboard {
  items: ModelItem[];
  viewItems: ViewItem[];
  connectors: ConnectorModel[];
  rectangles: RectangleModel[];
  textBoxes: TextBoxModel[];
}

let editorClipboard: EditorClipboard | null = null;

const getModeFunction = (mode: ModeActions, e: SlimMouseEvent) => {
  switch (e.type) {
    case 'mousemove':
      return mode.mousemove;
    case 'mousedown':
      return mode.mousedown;
    case 'mouseup':
      return mode.mouseup;
    default:
      return null;
  }
};

export const useInteractionManager = (enabled = true) => {
  const sceneStoreActions = useSceneStore((state) => state.actions);
  const temporaryPanModeRef = useRef<Mode | null>(null);
  const rendererRef = useRef<HTMLElement>();
  const reducerTypeRef = useRef<string>();
  const previousModeRef = useRef<Mode | null>(null);
  const rightButtonDownRef = useRef<boolean>(false);
  const rightButtonMovedRef = useRef<boolean>(false);
  const rightClickItemRef = useRef<ItemReference | null>(null);
  const rightClickTileRef = useRef<{ x: number; y: number } | null>(null);
  const rightClickPositionRef = useRef<{ x: number; y: number } | null>(null);
  const pinchGestureRef = useRef<{
    distance: number;
    zoom: number;
    center: { x: number; y: number };
    scroll: State['uiState']['scroll'];
  } | null>(null);
  const groupActions = useGroupStore((state) => state.actions);
  const uiState = useUiStateStore((state) => {
    return state;
  });
  const latestMouseRef = useRef(uiState.mouse);
  latestMouseRef.current = uiState.mouse;
  const model = useModelStore((state) => {
    return state;
  });
  const scene = useScene();
  const { size: rendererSize } = useResizeObserver(uiState.rendererEl);
  const interactionSnapshotRef = useRef({ model, scene, uiState, rendererSize });
  interactionSnapshotRef.current = { model, scene, uiState, rendererSize };
  const viewportRef = useRef({ zoom: uiState.zoom, scroll: uiState.scroll });
  viewportRef.current = { zoom: uiState.zoom, scroll: uiState.scroll };

  const onMouseEvent = useCallback(
    (e: SlimMouseEvent) => {
      // Mouse state changes on every pointer event. Read the latest stores from
      // a ref so this callback stays stable and the window listeners are not
      // removed/re-added after every setMouse update.
      const { model, scene, uiState, rendererSize } = interactionSnapshotRef.current;
      if (!rendererRef.current) return;
      const isFinishingCanvasDrag = e.type === 'mouseup' &&
        (uiState.mode.type === 'DRAG_ITEMS' || rightButtonDownRef.current);
      if (isEditorUiEvent(e.target) && !isFinishingCanvasDrag) return;

      // Track right button state
      if (e.type === 'mousedown' && e.button === 2) {
        rightButtonDownRef.current = true;
        rightButtonMovedRef.current = false;
        const rightMouse = getMouse({
          interactiveElement: rendererRef.current,
          zoom: uiState.zoom,
          scroll: uiState.scroll,
          lastMouse: latestMouseRef.current,
          mouseEvent: e,
          rendererSize
        });
        latestMouseRef.current = rightMouse;
        const sceneElement = e.target instanceof Element
          ? e.target.closest<HTMLElement>('[data-scene-item-id][data-scene-item-type]')
          : null;
        const elementId = sceneElement?.dataset.sceneItemId;
        const elementType = sceneElement?.dataset.sceneItemType as ItemReference['type'] | undefined;
        rightClickItemRef.current = elementId && elementType
          ? { id: elementId, type: elementType }
          : getItemsAtTile({ tile: rightMouse.position.tile, scene })[0] ?? null;
        rightClickTileRef.current = rightMouse.position.tile;
        rightClickPositionRef.current = { x: e.clientX, y: e.clientY };
        uiState.actions.setMouse(rightMouse);
        // Right-click is reserved for contextual actions and panning. Never
        // pass it into cursor/item drag modes.
        return;
      }
      if (e.type === 'mouseup' && e.button === 2) {
        rightButtonDownRef.current = false;
        if (
          !rightButtonMovedRef.current &&
          rightClickItemRef.current &&
          rightClickTileRef.current &&
          rightClickPositionRef.current
        ) {
          uiState.actions.setItemControls(rightClickItemRef.current);
          uiState.actions.setContextMenu({
            item: rightClickItemRef.current,
            tile: rightClickTileRef.current,
            position: rightClickPositionRef.current
          });
        }
        rightClickItemRef.current = null;
        rightClickTileRef.current = null;
        rightClickPositionRef.current = null;
        // Exit PAN mode when right button is released
        if (uiState.mode.type === 'PAN' && previousModeRef.current) {
          uiState.actions.setMode(previousModeRef.current);
          previousModeRef.current = null;
        }
        return;
      }

      // A new primary-button interaction owns the pointer immediately. Clear
      // controls belonging to a different previous selection on mousedown,
      // rather than waiting for mouseup or the first drag movement. Ctrl/Cmd
      // interactions retain the previous selection for multi-select toggling.
      if (
        e.type === 'mousedown' &&
        e.button === 0 &&
        !e.ctrlKey &&
        !e.metaKey
      ) {
        const pressedMouse = getMouse({
          interactiveElement: rendererRef.current,
          zoom: uiState.zoom,
          scroll: uiState.scroll,
          lastMouse: latestMouseRef.current,
          mouseEvent: e,
          rendererSize
        });
        const sceneElement = e.target instanceof Element
          ? e.target.closest<HTMLElement>('[data-scene-item-id][data-scene-item-type]')
          : null;
        const elementId = sceneElement?.dataset.sceneItemId;
        const elementType = sceneElement?.dataset.sceneItemType as ItemReference['type'] | undefined;
        let pressedItem: ItemReference | null = elementId && elementType
          ? { id: elementId, type: elementType }
          : getItemsAtTile({ tile: pressedMouse.position.tile, scene })[0] ?? null;

        if (!pressedItem) {
          uiState.actions.setItemControls(null);
          uiState.actions.setSelectionCycle(null);
        } else if (pressedItem.type === 'ITEM') {
          const group = groupActions.getGroupForElement({
            type: 'ITEM',
            id: pressedItem.id
          });
          if (group?.locked) {
            const groupItems = groupActions.getGroupElements(group.id).map((element) => ({
              type: element.type,
              id: element.id
            }));
            uiState.actions.setItemControls(groupItems.length > 1
              ? { type: 'MULTI_SELECT', items: groupItems }
              : pressedItem);
          } else {
            uiState.actions.setItemControls(pressedItem);
          }
        } else {
          uiState.actions.setItemControls(pressedItem);
        }
      }

      // Exit PAN mode on left-click (but only if PAN was entered temporarily via right-click)
      // In read-only mode (EXPLORABLE_READONLY), PAN is the permanent mode, so don't exit
      if (e.type === 'mousedown' && e.button === 0 && uiState.contextMenu) {
        uiState.actions.setContextMenu(null);
      }
      if (e.type === 'mousedown' && e.button === 0 && uiState.mode.type === 'PAN') {
        // Only exit PAN if we have a previous mode to return to (temporary PAN via right-click)
        if (previousModeRef.current) {
          uiState.actions.setMode(previousModeRef.current);
          previousModeRef.current = null;
          return;
        }
        // If no previous mode (permanent PAN in read-only), continue to allow panning
      }

      const mode = modes[uiState.mode.type];
      const modeFunction = getModeFunction(mode, e);

      if (!modeFunction) return;

      const nextMouse = getMouse({
        interactiveElement: rendererRef.current,
        zoom: uiState.zoom,
        scroll: uiState.scroll,
        lastMouse: latestMouseRef.current,
        mouseEvent: e,
        rendererSize
      });

      // Enter PAN mode if right button is held and mouse has moved
      if (
        e.type === 'mousemove' &&
        rightButtonDownRef.current &&
        uiState.mode.type !== 'PAN' &&
        hasMovedTile(nextMouse)
      ) {
        rightButtonMovedRef.current = true;
        previousModeRef.current = uiState.mode;
        uiState.actions.setMode({
          type: 'PAN',
          showCursor: true
        });
        // Do not run the cursor's mousemove handler for the same event that
        // switches to pan; it could otherwise begin dragging the hit item.
        return;
      }

      latestMouseRef.current = nextMouse;
      uiState.actions.setMouse(nextMouse);

      const canvasElement = uiState.rendererEl;
      const canvasBounds = canvasElement?.getBoundingClientRect();
      // Events are registered on window so drags can finish outside the canvas.
      // Do not use the interaction overlay as the hit boundary: rendered scene
      // layers are siblings of that overlay and would otherwise be rejected.
      const isInsideCanvas = Boolean(
        canvasElement &&
        canvasBounds &&
        e.clientX >= canvasBounds.left &&
        e.clientX <= canvasBounds.right &&
        e.clientY >= canvasBounds.top &&
        e.clientY <= canvasBounds.bottom
      );

      const eventUiState = { ...uiState, mouse: nextMouse };
      const baseState: State = {
        model,
        scene,
        uiState: eventUiState,
        rendererRef: rendererRef.current,
        rendererSize,
        isRendererInteraction: isInsideCanvas,
        mouseEvent: e
      };

      if (reducerTypeRef.current !== uiState.mode.type) {
        const prevReducer = reducerTypeRef.current
          ? modes[reducerTypeRef.current]
          : null;

        if (prevReducer && prevReducer.exit) {
          prevReducer.exit(baseState);
        }

        if (mode.entry) {
          mode.entry(baseState);
        }
      }

      modeFunction(baseState);
      reducerTypeRef.current = uiState.mode.type;
    },
    [groupActions]
  );

  const onContextMenu = useCallback(
    (e: SlimMouseEvent) => {
      if (isEditorUiEvent(e.target)) return;
      e.preventDefault();
    },
    []
  );

  useEffect(() => {
    if (!enabled || uiState.mode.type === 'INTERACTIONS_DISABLED') return;

    const el = window;
    const onWindowBlur = () => {
      // Browsers do not guarantee a mouseup when the window loses focus. Drop
      // any stored press ownership so the previous element cannot capture the
      // next interaction when focus returns.
      const releasedMouse = {
        ...latestMouseRef.current,
        mousedown: null,
        delta: null
      };
      latestMouseRef.current = releasedMouse;
      uiState.actions.setMouse(releasedMouse);
      rightButtonDownRef.current = false;
      rightButtonMovedRef.current = false;

      if (uiState.mode.type === 'DRAG_ITEMS') {
        uiState.actions.setDropTargetGroupId(null);
        uiState.actions.setSmartGuides([]);
        uiState.actions.setMode({
          type: 'CURSOR',
          showCursor: true,
          mousedownItem: null
        });
      } else if (uiState.mode.type === 'PAN' && previousModeRef.current) {
        uiState.actions.setMode(previousModeRef.current);
        previousModeRef.current = null;
      }
    };

    // Helper function to calculate distance between two touches
    const getTouchDistance = (touches: TouchList): number => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Helper function to get center point between two touches
    const getTouchCenter = (touches: TouchList): { x: number; y: number } => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      // Multi-touch: pinch-to-zoom
      if (e.touches.length === 2) {
        e.preventDefault();
        if (!uiState.rendererEl) return;
        const rect = uiState.rendererEl.getBoundingClientRect();
        const center = getTouchCenter(e.touches);
        const viewport = viewportRef.current;
        pinchGestureRef.current = {
          distance: getTouchDistance(e.touches),
          zoom: viewport.zoom,
          center: { x: center.x - rect.left, y: center.y - rect.top },
          scroll: viewport.scroll
        };
      }
      // Single touch: convert to mouse event for panning
      else if (e.touches.length === 1) {
        // Prevent default browser touch behavior (scrolling, text selection, etc.)
        // This is crucial for custom pan to work on mobile
        e.preventDefault();
        onMouseEvent({
          ...e,
          clientX: Math.floor(e.touches[0].clientX),
          clientY: Math.floor(e.touches[0].clientY),
          type: 'mousedown',
          button: 0
        });
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      // Multi-touch: handle pinch-to-zoom
      if (e.touches.length === 2 && pinchGestureRef.current) {
        e.preventDefault();

        const gesture = pinchGestureRef.current;
        const currentDistance = getTouchDistance(e.touches);
        const scale = currentDistance / gesture.distance;
        const newZoom = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, gesture.zoom * scale)
        );

        if (uiState.rendererEl && rendererRef.current) {
          const rect = uiState.rendererEl.getBoundingClientRect();
          const currentCenter = getTouchCenter(e.touches);
          const center = {
            x: currentCenter.x - rect.left,
            y: currentCenter.y - rect.top
          };
          const anchoredScroll = getZoomedScrollPosition({
            anchor: gesture.center,
            rendererSize,
            scroll: gesture.scroll,
            oldZoom: gesture.zoom,
            newZoom
          });
          const nextScroll = {
            position: {
              x: anchoredScroll.x + center.x - gesture.center.x,
              y: anchoredScroll.y + center.y - gesture.center.y
            },
            offset: gesture.scroll.offset
          };

          viewportRef.current = { zoom: newZoom, scroll: nextScroll };
          uiState.actions.setZoom(newZoom);
          uiState.actions.setScroll(nextScroll);
        }
      }
      // Single touch: convert to mouse move for panning
      else if (e.touches.length === 1) {
        // Prevent default browser touch behavior (scrolling, etc.)
        // This is crucial for smooth pan on mobile
        e.preventDefault();
        onMouseEvent({
          ...e,
          clientX: Math.floor(e.touches[0].clientX),
          clientY: Math.floor(e.touches[0].clientY),
          type: 'mousemove',
          button: 0
        });
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      // Reset pinch state when touches end
      if (e.touches.length < 2) {
        pinchGestureRef.current = null;
      }

      // If no touches remain, trigger mouseup
      if (e.touches.length === 0) {
        onMouseEvent({
          ...e,
          clientX: 0,
          clientY: 0,
          type: 'mouseup',
          button: 0
        });
      }
    };

    const onScroll = (e: WheelEvent) => {
      e.preventDefault();

      if (!uiState.rendererEl || !rendererRef.current) return;

      // Get cursor position relative to renderer
      const rect = uiState.rendererEl.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      // Calculate world position under cursor (before zoom)
      const { zoom: oldZoom, scroll: oldScroll } = viewportRef.current;
      const newZoom = getWheelZoom(
        oldZoom,
        e.deltaY,
        e.deltaMode,
        rendererSize.height
      );

      // Only proceed if zoom actually changed
      if (newZoom === oldZoom) return;

      const nextScroll = {
        position: getZoomedScrollPosition({
          anchor: { x: cursorX, y: cursorY },
          rendererSize,
          scroll: oldScroll,
          oldZoom,
          newZoom
        }),
        offset: oldScroll.offset
      };

      viewportRef.current = { zoom: newZoom, scroll: nextScroll };
      uiState.actions.setZoom(newZoom);
      uiState.actions.setScroll(nextScroll);
    };

    const getSelectedItems = (): ItemReference[] => {
      if (uiState.itemControls?.type === 'MULTI_SELECT') {
        return uiState.itemControls.items;
      }
      if (uiState.mode.type === 'DRAG_ITEMS') return uiState.mode.items;
      if (uiState.itemControls && 'id' in uiState.itemControls) {
        return [uiState.itemControls];
      }
      return [];
    };

    const copySelection = () => {
      const selected = getSelectedItems();
      const view = model.views.find((candidate) => candidate.id === uiState.view) ?? model.views[0];
      if (!view || selected.length === 0) return false;
      const idsFor = (type: ItemReference['type']) =>
        new Set(selected.filter((item) => item.type === type).map((item) => item.id));
      const itemIds = idsFor('ITEM');
      editorClipboard = structuredClone({
        items: model.items.filter((item) => itemIds.has(item.id)),
        viewItems: view.items.filter((item) => itemIds.has(item.id)),
        connectors: (view.connectors ?? []).filter((item) => idsFor('CONNECTOR').has(item.id)),
        rectangles: (view.rectangles ?? []).filter((item) => idsFor('RECTANGLE').has(item.id)),
        textBoxes: (view.textBoxes ?? []).filter((item) => idsFor('TEXTBOX').has(item.id))
      });
      return true;
    };

    const pasteSelection = () => {
      if (!editorClipboard) return;
      const viewIndex = model.views.findIndex((candidate) => candidate.id === uiState.view);
      const targetViewIndex = viewIndex >= 0 ? viewIndex : 0;
      if (!model.views[targetViewIndex]) return;

      const idMap = new Map<string, string>();
      const mapId = (id: string) => {
        const existing = idMap.get(id);
        if (existing) return existing;
        const next = generateId();
        idMap.set(id, next);
        return next;
      };
      const offset = (tile: { x: number; y: number }) => ({ x: tile.x + 1, y: tile.y + 1 });
      const newItems = editorClipboard.items.map((item) => ({ ...item, id: mapId(item.id) }));
      const newViewItems = editorClipboard.viewItems.map((item) => ({ ...item, id: mapId(item.id), tile: offset(item.tile) }));
      const newRectangles = editorClipboard.rectangles.map((item) => ({ ...item, id: mapId(item.id), from: offset(item.from), to: offset(item.to) }));
      const newTextBoxes = editorClipboard.textBoxes.map((item) => ({ ...item, id: mapId(item.id), tile: offset(item.tile) }));
      const newConnectors = editorClipboard.connectors.map((item) => ({
        ...item,
        id: mapId(item.id),
        anchors: item.anchors.map((anchor) => ({
          ...anchor,
          id: generateId(),
          ref: {
            ...anchor.ref,
            ...(anchor.ref.item && idMap.has(anchor.ref.item) ? { item: idMap.get(anchor.ref.item) } : {}),
            ...(anchor.ref.tile ? { tile: offset(anchor.ref.tile) } : {})
          }
        }))
      }));

      const views = structuredClone(model.views);
      const targetView = views[targetViewIndex];
      targetView.items = [...newViewItems, ...targetView.items];
      targetView.rectangles = [...newRectangles, ...(targetView.rectangles ?? [])];
      targetView.textBoxes = [...newTextBoxes, ...(targetView.textBoxes ?? [])];
      // Connectors go through the existing reducer below so their rendered paths
      // and model anchors stay synchronized.
      model.actions.set({ items: [...model.items, ...newItems], views });
      newConnectors.forEach((connector) => scene.createConnector(connector));

      const selection: ItemReference[] = [
        ...newViewItems.map((item) => ({ type: 'ITEM' as const, id: item.id })),
        ...newRectangles.map((item) => ({ type: 'RECTANGLE' as const, id: item.id })),
        ...newTextBoxes.map((item) => ({ type: 'TEXTBOX' as const, id: item.id })),
        ...newConnectors.map((item) => ({ type: 'CONNECTOR' as const, id: item.id }))
      ];
      uiState.actions.setItemControls(selection.length === 1 ? selection[0] : { type: 'MULTI_SELECT', items: selection });
    };

    const deleteSelection = () => {
      getSelectedItems().forEach((item) => {
        if (item.type === 'ITEM') {
          scene.deleteViewItem(item.id);
          scene.deleteModelItem(item.id);
        } else if (item.type === 'CONNECTOR') scene.deleteConnector(item.id);
        else if (item.type === 'RECTANGLE') scene.deleteRectangle(item.id);
        else if (item.type === 'TEXTBOX') scene.deleteTextBox(item.id);
      });
      uiState.actions.setItemControls(null);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const isCtrl = e.ctrlKey || e.metaKey; // Support both Ctrl (Windows/Linux) and Cmd (Mac)

      const activeElement = document.activeElement;
      const isTyping = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA' || activeElement?.getAttribute('contenteditable') === 'true';

      if (e.key === 'Escape') {
        e.preventDefault();
        uiState.actions.setMode({ type: 'CURSOR', showCursor: true, mousedownItem: null });
        uiState.actions.setItemControls(null);
        uiState.actions.setContextMenu(null);
        return;
      }
      if (isTyping) return;

      if (isCtrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          model.actions.redo();
          if (sceneStoreActions.canRedo()) sceneStoreActions.redo();
        } else {
          model.actions.undo();
          if (sceneStoreActions.canUndo()) sceneStoreActions.undo();
        }
        uiState.actions.setItemControls(null);
        return;
      }
      if (isCtrl && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        model.actions.redo();
        if (sceneStoreActions.canRedo()) sceneStoreActions.redo();
        uiState.actions.setItemControls(null);
        return;
      }
      if (isCtrl && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        const view = model.views.find((candidate) => candidate.id === uiState.view) ?? model.views[0];
        if (!view) return;
        const items: ItemReference[] = [
          ...view.items.map((item) => ({ type: 'ITEM' as const, id: item.id })),
          ...(view.rectangles ?? []).map((item) => ({ type: 'RECTANGLE' as const, id: item.id })),
          ...(view.textBoxes ?? []).map((item) => ({ type: 'TEXTBOX' as const, id: item.id })),
          ...(view.connectors ?? []).map((item) => ({ type: 'CONNECTOR' as const, id: item.id }))
        ];
        uiState.actions.setItemControls({ type: 'MULTI_SELECT', items });
        return;
      }
      if (isCtrl && e.key.toLowerCase() === 'c') { e.preventDefault(); copySelection(); return; }
      if (isCtrl && e.key.toLowerCase() === 'x') { e.preventDefault(); if (copySelection()) deleteSelection(); return; }
      if (isCtrl && e.key.toLowerCase() === 'v') { e.preventDefault(); pasteSelection(); return; }
      if (isCtrl && e.key.toLowerCase() === 'd') { e.preventDefault(); if (copySelection()) pasteSelection(); return; }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const amount = e.shiftKey ? 5 : 1;
        const delta = e.key === 'ArrowUp' ? { x: 0, y: -amount } : e.key === 'ArrowDown' ? { x: 0, y: amount } : e.key === 'ArrowLeft' ? { x: -amount, y: 0 } : { x: amount, y: 0 };
        const view = model.views.find((candidate) => candidate.id === uiState.view) ?? model.views[0];
        if (!view) return;
        getSelectedItems().forEach((item) => {
          if (item.type === 'ITEM') {
            const value = view.items.find((candidate) => candidate.id === item.id);
            if (value && !value.locked) scene.updateViewItem(item.id, { tile: { x: value.tile.x + delta.x, y: value.tile.y + delta.y } });
          } else if (item.type === 'TEXTBOX') {
            const value = (view.textBoxes ?? []).find((candidate) => candidate.id === item.id);
            if (value && !value.locked) scene.updateTextBox(item.id, { tile: { x: value.tile.x + delta.x, y: value.tile.y + delta.y } });
          } else if (item.type === 'RECTANGLE') {
            const value = (view.rectangles ?? []).find((candidate) => candidate.id === item.id);
            if (value && !value.locked) scene.updateRectangle(item.id, { from: { x: value.from.x + delta.x, y: value.from.y + delta.y }, to: { x: value.to.x + delta.x, y: value.to.y + delta.y } });
          }
        });
        return;
      }

      // === GROUPING SHORTCUTS ===

      // Ctrl+G - Group Selected Elements
      if (isCtrl && e.key === 'g' && !e.shiftKey) {
        e.preventDefault();

        // Collect selected elements
        const selectedElements: GroupableElement[] = [];

        // Check multi-selection (via itemControls)
        if (uiState.itemControls && uiState.itemControls.type === 'MULTI_SELECT') {
          uiState.itemControls.items.forEach(item => {
            if (item.type === 'ITEM' || item.type === 'RECTANGLE' ||
              item.type === 'TEXTBOX' || item.type === 'CONNECTOR') {
              selectedElements.push({
                type: item.type as any,
                id: item.id
              });
            }
          });
        }
        // Check multi-selection (drag mode)
        else if (uiState.mode.type === 'DRAG_ITEMS' && uiState.mode.items) {
          uiState.mode.items.forEach(item => {
            if (item.type === 'ITEM' || item.type === 'RECTANGLE' ||
              item.type === 'TEXTBOX' || item.type === 'CONNECTOR') {
              selectedElements.push({
                type: item.type as any,
                id: item.id
              });
            }
          });
        }
        // Check single selection
        else if (uiState.itemControls) {
          const { itemControls } = uiState;
          if (itemControls.type === 'ITEM' || itemControls.type === 'RECTANGLE' ||
            itemControls.type === 'TEXTBOX' || itemControls.type === 'CONNECTOR') {
            selectedElements.push({
              type: itemControls.type as any,
              id: itemControls.id
            });
          }
        }

        // Create group if we have 2+ elements
        if (selectedElements.length >= 2) {
          // Check if any element is already grouped
          const hasGroupedElement = selectedElements.some(el =>
            groupActions.isGrouped(el)
          );

          if (hasGroupedElement) {
            console.warn('⚠️ Cannot group: some elements are already in a group');
            return;
          }

          const result = groupActions.createGroup(
            selectedElements,
            `Group ${Date.now().toString(36).slice(-4)}`
          );

          if (result.success) {
            console.log('✅ Group created via Ctrl+G:', result.groupId);
          } else {
            console.error('❌ Failed to create group:', result.error);
          }
        } else {
          console.warn('⚠️ Select at least 2 elements to group');
        }

        return;
      }

      // Ctrl+Shift+G - Ungroup Selected Group
      if (isCtrl && e.key === 'g' && e.shiftKey) {
        e.preventDefault();

        // Check if a single element is selected
        let selectedElement: GroupableElement | null = null;

        if (uiState.itemControls) {
          const { itemControls } = uiState;
          if (itemControls.type === 'ITEM' || itemControls.type === 'RECTANGLE' ||
            itemControls.type === 'TEXTBOX' || itemControls.type === 'CONNECTOR') {
            selectedElement = {
              type: itemControls.type as any,
              id: itemControls.id
            };
          }
        }

        if (selectedElement) {
          // Check if this element is in a group
          const group = groupActions.getGroupForElement(selectedElement);

          if (group) {
            const result = groupActions.ungroup(group.id);

            if (result.success) {
              console.log('✅ Group ungrouped via Ctrl+Shift+G:', group.id);
            } else {
              console.error('❌ Failed to ungroup:', result.error);
            }
          } else {
            console.warn('⚠️ Selected element is not in a group');
          }
        } else {
          console.warn('⚠️ Select a grouped element to ungroup');
        }

        return;
      }

      // Handle Delete key (Delete or Backspace)
      if (e.key === 'Delete' || e.key === 'Backspace') {

        // Prevent default backspace navigation
        e.preventDefault();

        deleteSelection();
      }
    };

    const onDoubleClick = (e: MouseEvent) => {
      if (!rendererRef.current || !uiState.rendererEl || uiState.editorMode !== 'EDITABLE' || isEditorUiEvent(e.target)) return;
      const bounds = uiState.rendererEl.getBoundingClientRect();
      if (e.clientX < bounds.left || e.clientX > bounds.right || e.clientY < bounds.top || e.clientY > bounds.bottom) return;
      const mouse = getMouse({
        interactiveElement: rendererRef.current,
        zoom: uiState.zoom,
        scroll: uiState.scroll,
        lastMouse: latestMouseRef.current,
        mouseEvent: e,
        rendererSize
      });
      const candidates = getItemsAtTile({ tile: mouse.position.tile, scene });
      const editable = candidates.find((item) => item.type === 'TEXTBOX') ??
        candidates.find((item) => item.type === 'ITEM');
      if (!editable) return;
      e.preventDefault();
      if (editable.type === 'TEXTBOX') {
        uiState.actions.setItemControls({ type: 'TEXTBOX', id: editable.id });
        uiState.actions.setEditingTextBoxId(editable.id);
      } else {
        uiState.actions.setItemControls({ type: 'ITEM', id: editable.id });
        uiState.actions.setEditingModelItemId(editable.id);
      }
    };

    const onTemporaryPanKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if (e.code !== 'Space' || e.repeat || isTyping || uiState.editorMode !== 'EDITABLE') return;
      e.preventDefault();
      if (!temporaryPanModeRef.current && uiState.mode.type !== 'PAN') {
        temporaryPanModeRef.current = uiState.mode;
        uiState.actions.setMode({ type: 'PAN', showCursor: false });
      }
    };

    const onTemporaryPanKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !temporaryPanModeRef.current) return;
      e.preventDefault();
      const previousMode = temporaryPanModeRef.current;
      temporaryPanModeRef.current = null;
      uiState.actions.setMode(previousMode);
    };

    el.addEventListener('mousemove', onMouseEvent);
    el.addEventListener('mousedown', onMouseEvent);
    el.addEventListener('mouseup', onMouseEvent);
    el.addEventListener('blur', onWindowBlur);
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('dblclick', onDoubleClick);
    document.addEventListener('keydown', onTemporaryPanKeyDown);
    document.addEventListener('keyup', onTemporaryPanKeyUp);
    // Touch events must be non-passive to allow preventDefault()
    // This is crucial for preventing browser's default touch behavior (scrolling, etc.)
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    uiState.rendererEl?.addEventListener('wheel', onScroll, { passive: false });

    // ✨ NEW: Drag and drop support for groups
    const onDragOver = (e: DragEvent) => {
      e.preventDefault(); // Required to allow drop
      e.dataTransfer!.dropEffect = 'copy';
    };

    const onDrop = async (e: DragEvent) => {
      e.preventDefault();

      try {
        const iconId = e.dataTransfer?.getData('application/flowi-icon');
        if (iconId) {
          if (!rendererRef.current) return;
          // Native event listeners keep the closure from the render in which
          // they were registered. Read the live snapshot so occupied-tile
          // detection includes items created after that render.
          const current = interactionSnapshotRef.current;
          const icon = iconPackManager.getIconById(iconId);
          if (!icon) return;
          const mouse = getMouse({
            interactiveElement: rendererRef.current,
            zoom: current.uiState.zoom,
            scroll: current.uiState.scroll,
            lastMouse: current.uiState.mouse,
            mouseEvent: e,
            rendererSize: current.rendererSize
          });
          const itemAtDrop = getItemsAtTile({
            tile: mouse.position.tile,
            scene: current.scene
          }).find((candidate) => candidate.type === 'ITEM');

          if (itemAtDrop?.type === 'ITEM') {
            requestIconReplacement({
              existingItemId: itemAtDrop.id,
              newIconId: iconId,
              tile: mouse.position.tile
            });
            return;
          }

          const id = generateId();
          current.scene.createModelItem({ id, name: icon.name || 'Untitled', icon: iconId });
          current.scene.createViewItem({ ...VIEW_ITEM_DEFAULTS, id, tile: mouse.position.tile });
          current.uiState.actions.setItemControls({ type: 'ITEM', id });
          current.uiState.actions.setMode({ type: 'CURSOR', showCursor: true, mousedownItem: null });
          return;
        }

        const groupData = e.dataTransfer?.getData('application/flowi-group');
        if (!groupData) return;

        const { type, groupId } = JSON.parse(groupData);
        if (type !== 'master-group') return;

        // Check if renderer is ready
        if (!rendererRef.current) {
          console.warn('⚠️ Renderer not ready for drop');
          return;
        }

        // Get drop position in tile coordinates
        const mouse = getMouse({
          interactiveElement: rendererRef.current,
          zoom: uiState.zoom,
          scroll: uiState.scroll,
          lastMouse: uiState.mouse,
          mouseEvent: e,
          rendererSize
        });

        // Create group instance at drop position
        const instance = await groupActions.createGroupInstance(groupId, mouse.position.tile);
        if (instance) {
          console.log('✅ Group instance created from drag and drop:', instance);
          console.log('📍 Instance items to add:', instance.items);
          console.log('📍 Instance icons to add:', instance.icons);
          console.log('🔍 First item FULL structure:', JSON.stringify(instance.items[0], null, 2));
          console.log('🔍 First icon FULL structure:', JSON.stringify(instance.icons[0], null, 2));

          // Split items into ModelItems and ViewItems (Flowi's two-layer architecture)
          const modelItems = instance.items.map((item: any) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            icon: item.icon,
            color: item.color
          }));

          const viewItems = instance.items.map((item: any) => ({
            id: item.id,
            tile: item.tile,
            labelHeight: item.labelHeight,
            visible: item.visible,
            locked: item.locked,
            zIndex: item.zIndex
          }));

          // Get current state
          const currentItems = model.items || [];
          const currentIcons = model.icons || [];
          const currentColors = model.colors || [];
          const currentViews = model.views || [];
          const currentView = currentViews[0] || { id: 'default', name: 'Default View', items: [], rectangles: [], connectors: [], textBoxes: [] };

          console.log('📊 Current model items count:', currentItems.length);
          console.log('📊 Current model icons count:', currentIcons.length);
          console.log('📊 Current view items count:', currentView.items?.length || 0);

          // Update the model with separated data
          const newItems = [...currentItems, ...modelItems];
          const newIcons = [...currentIcons, ...instance.icons];
          const newColors = [...currentColors, ...instance.colors];
          const newViewItems = [...(currentView.items || []), ...viewItems];

          const updatedView = {
            ...currentView,
            items: newViewItems
          };

          const newViews = currentViews.length > 0
            ? [updatedView, ...currentViews.slice(1)]
            : [updatedView];

          model.actions.set({
            items: newItems,
            icons: newIcons,
            colors: newColors,
            views: newViews
          });

          console.log('✅ Added', instance.items.length, 'items,', instance.icons.length, 'icons,', instance.colors.length, 'colors from group instance to canvas');
          console.log('📊 New total: items:', newItems.length, 'icons:', newIcons.length, 'view items:', newViewItems.length);

          // Create a local group to keep the items together
          const groupElements = modelItems.map((item) => ({
            type: 'ITEM' as const,
            id: item.id
          }));

          const localGroupResult = groupActions.createGroup(
            groupElements,
            instance.name
          );

          if (localGroupResult.success && localGroupResult.groupId) {
            // Link the local group to the master group
            groupActions.setMasterGroupId(localGroupResult.groupId, instance.masterGroupId);
            console.log('✅ Created local group:', localGroupResult.groupId, 'linked to master:', instance.masterGroupId);
          } else {
            console.warn('⚠️ Failed to create local group:', localGroupResult.error);
          }
        }
      } catch (error) {
        console.error('❌ Failed to handle group drop:', error);
      }
    };

    el.addEventListener('dragover', onDragOver);
    el.addEventListener('drop', onDrop);

    // Document-level mouseup handler for PLACE_ICON mode (when drag starts from sidebar)
    // This is needed because canvas mouseup won't fire if mousedown happened outside
    const onDocumentMouseUp = (e: MouseEvent) => {
      if (uiState.mode.type !== 'PLACE_ICON') return;
      if (!rendererRef.current) return;

      // Check if mouse is over the renderer
      const rect = rendererRef.current.getBoundingClientRect();
      const isOverRenderer = (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );

      if (isOverRenderer) {
        // Simulate the mouse event on the renderer
        onMouseEvent(e as unknown as SlimMouseEvent);
      }
    };

    document.addEventListener('mouseup', onDocumentMouseUp);

    return () => {
      el.removeEventListener('mousemove', onMouseEvent);
      el.removeEventListener('mousedown', onMouseEvent);
      el.removeEventListener('mouseup', onMouseEvent);
      el.removeEventListener('blur', onWindowBlur);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('dblclick', onDoubleClick);
      document.removeEventListener('keydown', onTemporaryPanKeyDown);
      document.removeEventListener('keyup', onTemporaryPanKeyUp);
      // Must use same options as addEventListener for proper cleanup
      el.removeEventListener('touchstart', onTouchStart, { passive: false } as any);
      el.removeEventListener('touchmove', onTouchMove, { passive: false } as any);
      el.removeEventListener('touchend', onTouchEnd, { passive: false } as any);
      uiState.rendererEl?.removeEventListener('wheel', onScroll);
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('drop', onDrop);
      document.removeEventListener('mouseup', onDocumentMouseUp);

    };
  }, [
    enabled,
    uiState.editorMode,
    onMouseEvent,
    uiState.mode.type,
    onContextMenu,
    uiState.actions,
    uiState.rendererEl,
    uiState.itemControls
  ]);

  const setInteractionsElement = useCallback((element: HTMLElement) => {
    rendererRef.current = element;
  }, []);

  return {
    setInteractionsElement
  };
};

const isEditorUiEvent = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('[data-editor-ui="true"]'));
